import { getAnalyticsPath, getAnalyticsUrl, getLocalId, getSanitizedMarketingSearchParams } from 'Lib/AppUtils'
import { mergeEmails, normalizeUserData } from 'Lib/GTagUtils'

const GA_MEASUREMENT_ID_PRODUCTION = 'G-ME1FB0LY2H'
const GA_MEASUREMENT_ID_STAGING = 'G-MLGSX96V36'

// Google Ads manager (parent/MCC) account ID used for cross-account conversion tracking per Google recommendations.
// Do not replace this with a client account ID, or cross-account conversion tracking will break.
const AW_CONVERSION_ID = 'AW-786447737'

const GTAG_SERVER_SIDE_URL = 'https://routing4.bodyfast.app'

export enum AW_CONVERSION_LABELS {
  PURCHASE = 'HBYsCN6N1ekbEPn6gPcC' // This is the id for "Purchase" conversion action under AW-786447737 account: https://ads.google.com/aw/conversions/detail?ocid=287782996&ascid=287782996&ctId=7469352670&showWebpagesTab=true&showDiagnosticsTab=false&showStoreDiagnosticsTab=false&euid=972524107&__u=6659601843&uscid=287782996&__c=3893543604&authuser=0
}

const DEFAULT_EVENT_TIMEOUT = 60000 // 60 seconds

interface IPurchaseInfo {
  affiliation: 'sepa' | 'card' | 'paypal'
  currency: string
  value: number
  items: string[]
  transaction_id: string
}

export interface IGTagService {
  updateConsent: (performance: boolean, targeting: boolean) => void
  logPurchase: (info: IPurchaseInfo) => void
  logEvent: (eventName: string, params?: Record<string, any>) => void
  setPageData: (path: string, location: string) => void
  pageView: (path: string, location: string) => void
  setParams: (params: Object) => void
  setUserData: (userData: IGTagUserData) => void
  setUserProperties: (properties: Record<string, any>) => void
  setUserId: (userId: string) => void
}

let userData: IGTagUserData = {}

export function createGTagService (): IGTagService {
  window.dataLayer = window.dataLayer || []

  function gtag (...args: any) {
    window.dataLayer.push(arguments)
  }

  // The consent default must precede every config command.
  // From: https://developers.google.com/tag-platform/security/guides/consent?consentmode=advanced
  // "Consent mode users need to send two new parameters in addition to ad_storage and analytics_storage"
  // (text goes on telling the other two consents are ad_personalization and ad_user_data)
  gtag('consent', 'default', {
    analytics_storage: 'denied',
    ad_storage: 'denied',
    ad_personalization: 'denied',
    ad_user_data: 'denied',
    wait_for_update: 500
  })

  // Global, before `js` and every config: the Google tag's linked destinations
  // (the Firebase stream) write _ga too, a config's own cookie_flags skips them,
  // and their first write uses what was set before `js`.
  gtag('set', 'cookie_flags', 'SameSite=None;Secure')

  gtag('js', new Date())

  const analyticsPath = getAnalyticsPath()
  const analyticsUrl = getAnalyticsUrl()
  const searchParams = getSanitizedMarketingSearchParams()

  const doNotTrack = window.navigator.doNotTrack

  const CONFIG = {
    anonymize_ip: true,
    page_path: analyticsPath,
    page_location: analyticsUrl,
    referrer: analyticsUrl,
    gclid: searchParams.get('gclid'),
    dc_custom_params: JSON.stringify({
      // See dc_lat under "Custom Fields" section: https://support.google.com/campaignmanager/answer/7554821?hl=en#zippy=%2Cstep-add-the-global-snippet-to-every-page-of-your-site%2Cstep-add-the-event-snippet-to-pages-with-events-youre-tracking%2Cexample-event-snippet-for-sales---transaction-activities%2Ccustom-fields
      dc_lat: (doNotTrack === '1' || doNotTrack === 'yes') ? 1 : 0 // 1 means that the user has enabled the “Limit Ad Tracking
    })
  }

  if (!__DEV__) {
    gtag('config', AW_CONVERSION_ID, CONFIG)
  }

  const GA_MEASUREMENT_ID = __DEV__ ? GA_MEASUREMENT_ID_STAGING : GA_MEASUREMENT_ID_PRODUCTION

  gtag('config', GA_MEASUREMENT_ID, {
    ...CONFIG,
    transport_url: GTAG_SERVER_SIDE_URL,
    cookie_domain: 'bodyfast.app'
  })

  gtag('set', 'linker', {
    domains: [
      'localhost:3000',
      'my.bodyfast.app',
      'my2.bodyfast.app',
      'lp.bodyfast.de',
      'checkout.stripe.com',
      'paypal.com'
    ]
  })

  const GTag = {
    updateConsent: (performance: boolean, targeting: boolean) => {
      gtag('consent', 'update', {
        ad_storage: targeting ? 'granted' : 'denied',
        ad_personalization: targeting ? 'granted' : 'denied',
        ad_user_data: targeting ? 'granted' : 'denied',
        analytics_storage: performance ? 'granted' : 'denied'
      })
    },
    logPurchase: (info: IPurchaseInfo) => {
      gtag('event', 'purchase', {
        localId: getLocalId(),
        gclid: searchParams.get('gclid'),
        ...info,
        event_timeout: DEFAULT_EVENT_TIMEOUT // It's not clearly documented but default value can be 2 seconds. : https://developers.google.com/tag-platform/gtagjs/reference/parameters
      })

      gtag('event', 'conversion', {
        send_to: `${AW_CONVERSION_ID}/${AW_CONVERSION_LABELS.PURCHASE}`,
        gclid: searchParams.get('gclid'),
        ...info,
        event_timeout: DEFAULT_EVENT_TIMEOUT // It's not clearly documented but default value can be 2 seconds. : https://developers.google.com/tag-platform/gtagjs/reference/parameters
      })
    },
    logEvent: (eventName: string, params?: Record<string, any>) => {
      gtag('event', eventName, {
        ...params,
        gclid: searchParams.get('gclid'),
        event_timeout: DEFAULT_EVENT_TIMEOUT // It's not clearly documented but default value can be 2 seconds. : https://developers.google.com/tag-platform/gtagjs/reference/parameters
      })
    },
    pageView: (path: string, location: string) => {
      // Google Analytics automatically tracks page_view events with "optimized page load measurement" enabled.
      // gtag('event', 'page_view', {
      //   ...CONFIG,
      //   page_path: path,
      //   page_location: location,
      //   event_timeout: DEFAULT_EVENT_TIMEOUT // It's not clearly documented but default value can be 2 seconds. : https://developers.google.com/tag-platform/gtagjs/reference/parameters
      // })
    },
    setPageData: (path: string, location: string) => {
      gtag('set', {
        gclid: searchParams.get('gclid'),
        page_path: path,
        page_location: location
      })
    },
    setParams: (params: Object) => {
      gtag('set', {
        ...params
      })
    },
    setUserData: (newData: IGTagUserData) => {
      const normalized = normalizeUserData(newData)

      if (normalized.email?.length) {
        normalized.email = mergeEmails(userData.email ?? [], normalized.email)
      }

      userData = { ...userData, ...normalized }
      gtag('set', 'user_data', userData)
    },
    setUserProperties: (properties: Record<string, any>) => {
      gtag('set', 'user_properties', properties)
    },
    setUserId: (userId: string) => {
      gtag('set', { user_id: userId })
    }
  }

  return GTag
}
